import { RenderResult } from '@dojo/framework/core/interfaces';
import { create, tsx } from '@dojo/framework/core/vdom';

import Text from './Text';

import * as css from './ImageSection.m.css';

const bottom = require('../assets/images/header-section-backgrounds/section-mask-bottom-large.svg');

export interface ImageSectionProperties {
	/** maintain content width at smaller widths, clipping image as needed */
	contentBias?: boolean;
	hero?: boolean;
	reverse?: boolean;
	shaded?: boolean | 'desktop';
	responsiveMainImageHidden?: boolean;

	/** default: 24px, lg: 160px */
	paddingBottom?: 'none' | 'lg';

	/** xs: 84px, sm: 112px, md: 128px, lg: 144px */
	paddingTop?: 'xs' | 'sm' | 'md' | 'lg';

	/** small = 50%/50% columns; medium = 43.232%/56.768%; default is small */
	trailingBias?: 'small' | 'medium' | 'large';

	/** If true the element will be vertically shifted up to overlap the bottom of the above content */
	overlap?: boolean;

	/** Set all images to load lazy. Use this setting if the section is below the initial fold */
	lazy?: boolean;

	/** define the gutting on the image */
	imageGutter?: 'medium' | 'small';
}

interface ImageSectionChildren {
	title: RenderResult;
	blurb: RenderResult;
	button?: RenderResult;
	images: {
		main: RenderResult;
		top?: RenderResult;
		bottom?: {
			left: RenderResult;
			right: RenderResult;
		};
	};
}

const SectionRow = create()
	.properties<{
		paddingBottom?: ImageSectionProperties['paddingBottom'];
		paddingTop?: ImageSectionProperties['paddingTop'];
		reverse?: boolean;
		responsive?: boolean;
		trailingBias?: ImageSectionProperties['trailingBias'];
		imageGutter?: ImageSectionProperties['imageGutter'];
		hero?: boolean;
	}>()
	.children<{ content?: RenderResult; image: RenderResult }>()(function SectionRow({
	properties,
	children
}) {
	const {
		paddingBottom,
		reverse,
		responsive = true,
		trailingBias,
		hero = false,
		imageGutter = 'medium'
	} = properties();
	const [{ content, image }] = children();
	return (
		<div
			classes={[
				css.row,
				paddingBottom === 'lg' && css.paddingBottomLarge,
				paddingBottom === 'none' && css.paddingBottomNone,
				reverse && css.reverse,
				hero && css.hero,
				!responsive && css.hidden,
				trailingBias === 'medium' && css.mediumTrailingBias,
				trailingBias === 'large' && css.largeTrailingBias
			]}
		>
			<div classes={[css.left]}>
				<div
					classes={[
						css.content,
						reverse && css.reverse,
						imageGutter === 'medium' && css.imageGutterMedium,
						imageGutter === 'small' && css.imageGutterSmall
					]}
				>
					{content}
				</div>
			</div>
			<div classes={[css.right, hero && css.hero]}>{image}</div>
		</div>
	);
});

const factory = create().properties<ImageSectionProperties>().children<ImageSectionChildren>();

export const ImageSection = factory(function ImageSection({ properties, children }) {
	const {
		contentBias,
		hero,
		paddingBottom,
		paddingTop,
		responsiveMainImageHidden,
		reverse,
		shaded = false,
		trailingBias,
		overlap,
		lazy,
		imageGutter
	} = properties();
	const [
		{
			title,
			blurb,
			button,
			images: { main: mainImage, top: topImage, bottom: bottomImages }
		}
	] = children();
	const hasBottomImage = bottomImages?.left || bottomImages?.right;
	const loading = lazy ? 'lazy' : undefined;
	return (
		<div
			classes={[
				css.root,
				!hero && paddingTop === 'xs' && css.paddingTopXSmall,
				!hero && paddingTop === 'sm' && css.paddingTopSmall,
				!hero && paddingTop === 'md' && css.paddingTopMedium,
				!hero && paddingTop === 'lg' && css.paddingTopLarge,
				!hero && paddingBottom === 'lg' && css.paddingBottomLarge,
				contentBias && css.contentBias,
				overlap && css.topOffsetSmall
			]}
		>
			{shaded && <div classes={[css.top, css.desktopShaded]} />}
			<div
				classes={[
					css.middle,
					reverse && css.reverse,
					shaded && css.shaded,
					shaded === 'desktop' && css.desktopShaded
				]}
			>
				{topImage && (
					<SectionRow reverse={reverse} responsive={false} hero={hero}>
						{{
							image: (
								<div classes={[css.imageWrapper, reverse && css.reverse]}>
									<div classes={[css.topImage]}>{topImage}</div>
								</div>
							)
						}}
					</SectionRow>
				)}
				<SectionRow
					paddingBottom={hasBottomImage ? undefined : 'none'}
					paddingTop={paddingTop}
					reverse={reverse}
					trailingBias={trailingBias}
					hero={hero}
					imageGutter={imageGutter}
				>
					{{
						image: (
							<div
								classes={[
									css.imageWrapper,
									css.mainImageContainer,
									reverse && css.reverse,
									responsiveMainImageHidden && css.hidden
								]}
							>
								{mainImage}
							</div>
						),
						content: (
							<div classes={[css.description]}>
								{typeof title === 'string' ? (
									<Text
										type={hero ? 'hero-primary' : 'section'}
										responsive={hero ? 'callout-head' : undefined}
									>
										{title}
									</Text>
								) : (
									title
								)}
								<Text
									type={hero ? 'large-body' : 'body'}
									responsive={hero ? 'body' : undefined}
								>
									{blurb}
								</Text>
								{button && button}
							</div>
						)
					}}
				</SectionRow>
				{hasBottomImage && (
					<SectionRow
						reverse={reverse}
						responsive={false}
						paddingBottom="none"
						hero={hero}
					>
						{{
							image: (
								<div
									classes={[
										css.imageWrapper,
										reverse && css.reverse,
										reverse && css.bottomImageReverse
									]}
								>
									<div>
										{bottomImages?.right && (
											<div classes={[css.bottomImageRight]}>
												{bottomImages.right}
											</div>
										)}
										{bottomImages?.left && (
											<div
												classes={[
													css.bottomImageLeft,
													reverse && css.reverse
												]}
											>
												{bottomImages.left}
											</div>
										)}
									</div>
								</div>
							)
						}}
					</SectionRow>
				)}
			</div>
			{shaded && (
				<img
					classes={[css.bottom, css.desktopShaded]}
					src={bottom}
					loading={loading}
					alt=""
				/>
			)}
		</div>
	);
});

export default ImageSection;
